import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Page, Layout, ResourceList, Avatar, ResourceItem, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams, Outlet } from "react-router-dom";
import { AlertTriangle, Search } from "lucide-react";

import { fetchCustomers, setFilterValue, setShowAll } from "./customers.slice";
import { setModalxComponent } from "components/modalx/modalx.slice";
import Currency from "components/format/currency/currency";
import UserSelect from "features/user-select/user-select";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import { useCustomerSelection } from "./use-customer-selection";
import CustomersSelectionBar from "./customers-selection-bar";
import { isFinvaldaMismatch, finvaldaBalanceDifference } from "./finvalda-mismatch";
import useIsPhone from "features/files/use-is-phone";

// Plain "1234.56" for the mismatch tooltip — no currency symbol, this is a
// short technical aside, not a headline figure.
function formatDebtNumber(value) {
  const num = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(num) ? num.toFixed(2) : "-";
}

// Native checkbox with `indeterminate` support (not a plain React prop) for
// the "select all loaded rows" header control.
function HeaderCheckbox({ checked, indeterminate, onChange, ariaLabel }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="w-4 h-4 rounded border-gray-300 text-[#3F3227] focus:ring-[#3F3227]"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
    />
  );
}

function Customers() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customerId, companyId } = useParams();
  // Phones get cards; tablets and desktops get the table.
  const isPhone = useIsPhone();

  const {
    customersCollection,
    filtersValue,
    showAll,
    isLoading,
    hasLoaded,
    isRestrictedToIds,
  } = useSelector((state) => state.customersReducer);

  const { role, session } = useSelector((state) => state.authReducer);
  const isUserSalesMan = role?.permissionsgroups?.includes("SalesMan");
  // Session loads async — userId (and therefore the selection hook) starts
  // out null and picks up the persisted selection once it resolves.
  const userId = session?.id;

  const selection = useCustomerSelection(userId, companyId);
  // "Show only selected" is UI-only state, not persisted; it turns itself
  // off once the selection empties out (see effect below).
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const hasSelectionBar = selection.selectedIds.length > 0;

  // Skeleton only until the first response arrives. Once loaded, an empty
  // result shows the table's "no data" state (not the skeleton), and later
  // refetches (search, "show all") keep the current list visible.
  // Also while leaving "show only selected": the store still holds the
  // selection-only list until the full one arrives, and presenting it as the
  // full list would be wrong.
  const showSkeleton = !hasLoaded || (!showOnlySelected && isRestrictedToIds);

  // Keep the page header (title + filters) fixed and let only the customer
  // list scroll. We cap the DataTable body to the space left below the filters
  // (and the selection bar, when visible) so the page never overflows its
  // scroll container and the bar never covers the last rows.
  const filtersRef = useRef(null);
  const tableRef = useRef(null);
  const barRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState("calc(100vh - 260px)");

  useLayoutEffect(() => {
    if (isPhone) return undefined;

    const computeHeight = () => {
      const tableEl = tableRef.current;
      if (!tableEl) return;
      const top = tableEl.getBoundingClientRect().top;
      const barHeight = hasSelectionBar ? barRef.current?.offsetHeight || 0 : 0;
      const available = window.innerHeight - top - 24 - barHeight; // bottom breathing room
      setTableMaxHeight(`${Math.max(Math.round(available), 240)}px`);
    };

    computeHeight();
    window.addEventListener("resize", computeHeight);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(computeHeight);
      if (filtersRef.current) resizeObserver.observe(filtersRef.current);
      if (barRef.current) resizeObserver.observe(barRef.current);
    }

    return () => {
      window.removeEventListener("resize", computeHeight);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [hasSelectionBar, isPhone]);

  const handleCreateCustomer = () => {
    dispatch(setModalxComponent("CustomerForm"));
  };

  const handleShowAllToggle = (checked) => {
    dispatch(setShowAll(checked));
  };

  const handleReconciliationToggle = (checked) => {
    dispatch(
      setFilterValue({ key: "reconciliation", value: checked ? "mismatch" : null })
    );
  };

  useEffect(() => {
    dispatch(
      fetchCustomers(
        companyId,
        filtersValue,
        showAll,
        showOnlySelected ? selection.selectedIds : undefined
      )
    );
    // Selection edits while `showOnlySelected` is already on are handled by
    // the client-side filter below (unchecking hides a row immediately), not
    // by a refetch — only the on/off transition (and the other deps) refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filtersValue, companyId, showAll, showOnlySelected]);

  // Selection can empty out from under "show only selected" (Clear, or a
  // selected customer becoming unavailable) — fall back to the full list.
  useEffect(() => {
    if (showOnlySelected && selection.selectedIds.length === 0) {
      setShowOnlySelected(false);
    }
  }, [showOnlySelected, selection.selectedIds]);

  const handleToggleShowOnlySelected = () => {
    setShowOnlySelected((prev) => !prev);
  };

  const handleFilterChange = (value, key) => {
    dispatch(setFilterValue({ key, value }));
  };
  const handleQueryChange = (value) => {
    dispatch(setFilterValue({ key: "textSearch", value }));
  };
  let content = <Outlet />;

  // Rows as loaded from the server for the current filter/mode. When
  // `showOnlySelected` is on this is already restricted to the selection
  // (see the fetch effect), but we filter again client-side so unchecking a
  // row hides it immediately without waiting for a refetch.
  const loadedCollection = customersCollection.map((item) => ({
    ...item,
    displayName: [item.name, item.legalName].filter(Boolean).join(" "),
  }));
  const displayedCollection = showOnlySelected
    ? loadedCollection.filter((item) => selection.isSelected(item.id))
    : loadedCollection;

  const loadedIds = new Set(loadedCollection.map((item) => item.id));
  const hiddenSelectedCount = selection.selectedIds.filter(
    (id) => !loadedIds.has(id)
  ).length;

  const isAllLoadedSelected =
    displayedCollection.length > 0 &&
    displayedCollection.every((item) => selection.isSelected(item.id));
  const isSomeLoadedSelected =
    !isAllLoadedSelected &&
    displayedCollection.some((item) => selection.isSelected(item.id));

  const handleSelectAllLoaded = () => {
    selection.setMany(
      displayedCollection.map((item) => item.id),
      !isAllLoadedSelected
    );
  };

  const filtersNode = (
    <div className="flex flex-wrap items-center gap-3 rounded-sm border border-[#E6E1DB] bg-white px-3 py-2.5">
      <div className="relative min-w-[200px] flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={filtersValue.textSearch || ""}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder={t("search")}
          className="w-full rounded-sm border border-[#E6E1DB] py-1.5 pl-8 pr-3 text-base text-[#3F3227] placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#3F3227] max-md:min-h-11 md:text-sm"
        />
      </div>

      <div className="w-full max-w-[240px] sm:w-60">
        <UserSelect
          value={filtersValue.user}
          name="user"
          onChange={handleFilterChange}
          isRequired={false}
          allowMultiple={true}
          label="assigned_user"
          labelHidden
        />
      </div>

      <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-[#3F3227] max-md:min-h-11">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-[#3F3227] focus:ring-[#3F3227]"
          checked={showAll}
          onChange={(e) => handleShowAllToggle(e.target.checked)}
        />
        {t("show_all_customers")}
      </label>
      <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-[#3F3227] max-md:min-h-11">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-[#3F3227] focus:ring-[#3F3227]"
          checked={filtersValue.reconciliation === "mismatch"}
          onChange={(e) => handleReconciliationToggle(e.target.checked)}
        />
        {t("only_finvalda_mismatch")}
      </label>
    </div>
  );

  if (!customerId) {
    content = (
      <Page
        fullWidth
        primaryAction={{
          content: t("create_customer"),
          onAction: handleCreateCustomer,
        }}
        title={t("customers")}
        titleMetadata={<AuditLogPopover subjectType="Customer" />}
      >
        {isPhone && (
          <ResourceList
            resourceName={{ singular: t("customer"), plural: t("customers") }}
            items={customersCollection}
            loading={isLoading}
            filterControl={filtersNode}
            flushFilters
            renderItem={(item) => {
              const {
                id,
                assignedUser,
                name,
                legalName,
                externalCode,
                _debt,
                _overdueDebt,
                _turnover,
              } = item;
              const displayName = [name, legalName].filter(Boolean).join(" ");
              const hasMismatch = isFinvaldaMismatch(item);
              const media = <Avatar customer size="md" name={displayName} />;

              return (
                <ResourceItem
                  id={id}
                  url={id}
                  media={media}
                  accessibilityLabel={`View details for ${displayName}`}
                >
                  <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {displayName}
                  </Text>
                  <div>{assignedUser?.name || "-"}</div>
                  {externalCode && (
                    <div>
                      {t("customer_code")}: {externalCode}
                    </div>
                  )}
                  {(!!_debt || hasMismatch) && (
                    <div className="flex items-center gap-1">
                      {t("debt_incl_vat")}:{" "}
                      <span className={_debt < 0 ? "text-red-600" : undefined}>
                        <Currency isSymbol={true} value={_debt || 0} />
                      </span>
                      {hasMismatch && (
                        <AlertTriangle
                          className="h-4 w-4 shrink-0 text-amber-500"
                          aria-label={t("finvalda_mismatch_label")}
                        />
                      )}
                    </div>
                  )}
                  <div className="flex justify-space">
                    <div>
                      {_turnover > 0 && (
                        <Text alignment="start">
                          <Currency isSymbol={true} value={_turnover} />
                        </Text>
                      )}
                    </div>
                    <div>
                      {_overdueDebt > 0 && (
                        <Text alignment="end">
                          {t("debt")}:{" "}
                          <Currency isSymbol={true} value={_overdueDebt} />
                        </Text>
                      )}
                    </div>
                  </div>
                </ResourceItem>
              );
            }}
          />
        )}
        {!isPhone && (
          <div>
            <div ref={filtersRef} className="px-4 pt-4 pb-3">
              {filtersNode}
            </div>
            <div ref={tableRef}>
              {showSkeleton ? (
                <DataTableSkeleton rows={12} columns={8} />
              ) : (
                <DataTable
                  maxBodyHeight={tableMaxHeight}
                  rowUrl={(item) => `/app/${companyId}/customers/${item.id}`}
                  collection={displayedCollection}
                  columns={[
                    { key: "#" },
                    {
                      key: "_select",
                      type: "custom",
                      header: (
                        <HeaderCheckbox
                          checked={isAllLoadedSelected}
                          indeterminate={isSomeLoadedSelected}
                          onChange={handleSelectAllLoaded}
                          ariaLabel={t("select_all_rows")}
                        />
                      ),
                      render: (item) => (
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-[#3F3227] focus:ring-[#3F3227]"
                          checked={selection.isSelected(item.id)}
                          onChange={() => selection.toggle(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t("select_row")}
                        />
                      ),
                    },
                    {
                      key: "displayName",
                      name: t("title"),
                      url: (item) => `/app/${companyId}/customers/${item.id}`,
                    },
                    { key: "externalCode", name: t("customer_code") },
                    { key: "companyCode", name: t("company_code") },
                    { key: "vatCode", name: t("vat_code") },
                    { key: "assignedUser.name", name: t("assigned_user") },
                    {
                      key: "_turnover",
                      type: "currency",
                      name: t("sales_turnover_excl_vat"),
                    },
                    {
                      key: "_purchaseTurnover",
                      type: "currency",
                      name: t("purchase_turnover_excl_vat"),
                    },
                    {
                      key: "_debt",
                      type: "currency",
                      name: t("debt_incl_vat"),
                      // Becomes net (receivable − payable) once purchases are
                      // imported: negative = we owe this counterparty.
                      valueClassName: (item) =>
                        item._debt < 0 ? "text-red-600" : undefined,
                      // A fresh (< 24h) Finvalda balance that doesn't match our
                      // computed debt — Finvalda is the control figure, so a
                      // mismatch means something in the mirror was missed.
                      indicator: (item) =>
                        isFinvaldaMismatch(item) ? (
                          <AlertTriangle
                            className="h-4 w-4 shrink-0 text-amber-500"
                            aria-label={t("finvalda_mismatch_label")}
                            title={t("finvalda_mismatch_tooltip", {
                              balance: formatDebtNumber(item.finvaldaBalance),
                              difference: formatDebtNumber(
                                finvaldaBalanceDifference(item)
                              ),
                            })}
                          />
                        ) : null,
                    },
                    {
                      key: "_overdueDebt",
                      type: "currency",
                      name: t("overdue_debt_incl_vat"),
                      valueClassName: (item) =>
                        item._overdueDebt < 0 ? "text-red-600" : undefined,
                    },
                  ]}
                />
              )}
            </div>
            {hasSelectionBar && (
              <CustomersSelectionBar
                ref={barRef}
                companyId={companyId}
                selectedIds={selection.selectedIds}
                hiddenCount={hiddenSelectedCount}
                showOnlySelected={showOnlySelected}
                onToggleShowOnlySelected={handleToggleShowOnlySelected}
                onClear={selection.clear}
                onRemoveMissing={selection.remove}
              />
            )}
          </div>
        )}
      </Page>
    );
  }

  return (
    <Layout>
      <Layout.Section>{content}</Layout.Section>
    </Layout>
  );
}

export default Customers;
